export interface FuzzyMatch {
  score: number;
  indices: number[];
}

const MARKS = /[̀-ͯ]/g;

function foldChar(character: string): string {
  return character.normalize('NFD').replace(MARKS, '').toLowerCase().charAt(0);
}

function isWordStart(text: string, index: number): boolean {
  return index === 0 || /[\s(\-_/]/.test(text[index - 1]);
}

export function fuzzyMatch(query: string, text: string): FuzzyMatch | null {
  const needle = [...query].map(foldChar).filter((character) => character && character.trim());
  if (needle.length === 0) {
    return { score: 0, indices: [] };
  }

  const haystack = [...text].map(foldChar);
  const indices: number[] = [];
  let score = 0;
  let from = 0;

  for (const character of needle) {
    let found = -1;
    for (let index = from; index < haystack.length; index++) {
      if (haystack[index] !== character) {
        continue;
      }

      if (found === -1) {
        found = index;
      }

      if (isWordStart(text, index) && (indices.length === 0 || indices[indices.length - 1] !== index - 1)) {
        found = index;
        break;
      }

      if (indices.length > 0 && indices[indices.length - 1] === index - 1) {
        found = index;
        break;
      }
    }

    if (found === -1) {
      return null;
    }

    const previous = indices[indices.length - 1];
    score += 1;
    if (previous !== undefined && found === previous + 1) {
      score += 5;
    }

    if (isWordStart(text, found)) {
      score += 3;
    }

    if (found === 0) {
      score += 4;
    }

    if (previous !== undefined) {
      score -= Math.min(3, found - previous - 1) * 0.5;
    }

    indices.push(found);
    from = found + 1;
  }

  return { score: score - text.length * 0.01, indices };
}

export function highlightParts(text: string, indices: number[]): { text: string; matched: boolean }[] {
  const marked = new Set(indices);
  const parts: { text: string; matched: boolean }[] = [];

  [...text].forEach((character, index) => {
    const matched = marked.has(index);
    const last = parts[parts.length - 1];
    if (last && last.matched === matched) {
      last.text += character;
    } else {
      parts.push({ text: character, matched });
    }
  });

  return parts;
}
